/*
 * Insights and Books as React Query hooks (draft 10). Any change in Books refreshes every Books answer, since one new
 * category moves the transactions list, profit and loss, the balance sheet and the 1099s together.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AiReviewAnswer,
  Answer1099,
  AskAnswer,
  BalanceAnswer,
  BankAccountRow,
  BooksSetup,
  CashFlowAnswer,
  CategoryRow,
  CompanyStatsRow,
  FileRow,
  ForecastAnswer,
  InsightsAnswer,
  CancelsAnswer,
  LedgerLine,
  MatchesAnswer,
  MarketingCostRow,
  PlanEndsAnswer,
  PnlAnswer,
  ReconcileRow,
  RuleRow,
  TaxAnswer,
  TransactionRow,
} from "@steward/shared";
import { api } from "./client";
import { qs } from "./hooks";

// Insights -----------------------------------------------------------------------------------------------------------

/** Insights > Cancels: cancelled and skipped visits next to done ones. */
export const useCancels = (companyId: string, from: string, to: string) =>
  useQuery({
    queryKey: ["cancels", companyId, from, to],
    queryFn: () => api<CancelsAnswer>("GET", `insights/cancels${qs({ from, to })}`),
    placeholderData: keepPreviousData,
  });
export const useInsights = (companyId: string, from: string, to: string) =>
  useQuery({
    queryKey: ["insights", companyId, from, to],
    queryFn: () => api<InsightsAnswer>("GET", `insights${qs({ from, to })}`),
    placeholderData: keepPreviousData,
  });

export const usePlanEnds = (companyId: string) =>
  useQuery({ queryKey: ["plan-ends", companyId], queryFn: () => api<PlanEndsAnswer>("GET", "insights/plan-ends") });

export const useForecast = (companyId: string) =>
  useQuery({ queryKey: ["forecast", companyId], queryFn: () => api<ForecastAnswer>("GET", "insights/forecast") });

export const useCompanyStats = (companyId: string, from: string, to: string, enabled: boolean) =>
  useQuery({
    queryKey: ["insights-companies", companyId, from, to],
    queryFn: () => api<CompanyStatsRow[]>("GET", `insights/companies${qs({ from, to })}`),
    enabled,
  });

export const useCosts = (companyId: string, year: number) =>
  useQuery({
    queryKey: ["costs", companyId, year],
    queryFn: () => api<{ costs: MarketingCostRow[] }>("GET", `insights/costs${qs({ year: String(year) })}`),
  });

export function useSaveCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { month: string; channel: string; amountCents: number; note?: string }) =>
      api<MarketingCostRow>("PUT", "insights/costs", v),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: ["costs"] }), qc.invalidateQueries({ queryKey: ["insights"] })]),
  });
}

export function useRemoveCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>("DELETE", `insights/costs/${id}`),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: ["costs"] }), qc.invalidateQueries({ queryKey: ["insights"] })]),
  });
}

export const useAsked = (companyId: string) =>
  useQuery({ queryKey: ["ask", companyId], queryFn: () => api<{ asked: AskAnswer[] }>("GET", "ask") });

export function useAsk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (question: string) => api<AskAnswer>("POST", "ask", { question }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ask"] }),
  });
}

// Books --------------------------------------------------------------------------------------------------------------

const BOOKS = [
  "books",
  "books-tx",
  "pnl",
  "balance",
  "c1099",
  "adjustments",
  "books-companies",
  "books-ledger",
  "reconciles",
  "receipts",
  "books-match",
];

function useBooksChange<I, O>(fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(BOOKS.map((k) => qc.invalidateQueries({ queryKey: [k] })));
    },
  });
}

export const useBooks = (companyId: string) =>
  useQuery({ queryKey: ["books", companyId], queryFn: () => api<BooksSetup>("GET", "books") });

export interface TxFilter {
  from: string;
  to: string;
  accountId?: string;
  categoryId?: string;
  uncategorized?: boolean;
  state?: "pending" | "posted" | "excluded";
}
export const useBookTransactions = (companyId: string, f: TxFilter, enabled = true) =>
  useQuery({
    queryKey: ["books-tx", companyId, f],
    queryFn: () =>
      api<{ transactions: TransactionRow[] }>(
        "GET",
        `books/transactions${qs({
          from: f.from,
          to: f.to,
          accountId: f.accountId,
          categoryId: f.categoryId,
          uncategorized: f.uncategorized ? "1" : undefined,
          state: f.state,
        })}`,
      ),
    placeholderData: keepPreviousData,
    enabled,
  });

export const usePnl = (companyId: string, from: string, to: string) =>
  useQuery({
    queryKey: ["pnl", companyId, from, to],
    queryFn: () => api<PnlAnswer>("GET", `books/pnl${qs({ from, to })}`),
    placeholderData: keepPreviousData,
  });

export const useCashFlow = (companyId: string, from: string, to: string) =>
  useQuery({
    queryKey: ["cashflow", companyId, from, to],
    queryFn: () => api<CashFlowAnswer>("GET", `books/cash-flow${qs({ from, to })}`),
    placeholderData: keepPreviousData,
  });
export const useBalance = (companyId: string, day: string) =>
  useQuery({ queryKey: ["balance", companyId, day], queryFn: () => api<BalanceAnswer>("GET", `books/balance${qs({ day })}`) });

export const use1099 = (companyId: string, year: number) =>
  useQuery({
    queryKey: ["c1099", companyId, year],
    queryFn: () => api<Answer1099>("GET", `books/1099${qs({ year: String(year) })}`),
  });

export const useAdjustments = (companyId: string, year: number) =>
  useQuery({
    queryKey: ["adjustments", companyId, year],
    queryFn: () =>
      api<{
        adjustments: {
          id: string;
          day: string;
          memo: string;
          lines: { categoryId: string; amountCents: number }[];
          undone: boolean;
        }[];
      }>("GET", `books/adjustments${qs({ year: String(year) })}`),
  });

export const useSaveAccount = () =>
  useBooksChange(
    (v: {
      id?: string;
      name: string;
      kind: BankAccountRow["kind"];
      personal?: boolean;
      last4?: string | null;
      openingBalanceCents: number;
      openingDay?: string | null;
    }) => {
      const { id, ...body } = v;
      return id
        ? api<BankAccountRow>("PATCH", `books/accounts/${id}`, body)
        : api<BankAccountRow>("POST", "books/accounts", body);
    },
  );
export const useRemoveAccount = () => useBooksChange((id: string) => api<{ ok: true }>("DELETE", `books/accounts/${id}`));
export const useQuickBooksChart = () =>
  useBooksChange(() => api<{ renamed: number; added: number }>("POST", "books/categories/quickbooks"));
export const useAddCategory = () =>
  useBooksChange((v: { name: string; type: CategoryRow["type"]; parentId?: string }) =>
    api<CategoryRow>("POST", "books/categories", v),
  );
export const useHideCategory = () =>
  useBooksChange((v: { id: string; hidden: boolean }) =>
    api<{ ok: true }>("PATCH", `books/categories/${v.id}`, { hidden: v.hidden }),
  );
export interface RuleInput {
  match: string;
  categoryId: string;
  payee?: string;
  direction?: "in" | "out" | null;
  accountId?: string | null;
}
export const useAddRule = () =>
  useBooksChange((v: RuleInput) => api<{ rule: RuleRow; applied: number }>("POST", "books/rules", v));
export const useChangeRule = () =>
  useBooksChange((v: RuleInput & { id: string }) => {
    const { id, ...body } = v;
    return api<{ rule: RuleRow; applied: number }>("PATCH", `books/rules/${id}`, body);
  });
export const useRemoveRule = () => useBooksChange((id: string) => api<{ ok: true }>("DELETE", `books/rules/${id}`));
export const useChangeTransaction = () =>
  useBooksChange(
    (v: {
      id: string;
      categoryId?: string | null;
      note?: string;
      payee?: string;
      split?: { categoryId: string; amountCents: number }[] | null;
    }) => {
      const { id, ...body } = v;
      return api<TransactionRow>("PATCH", `books/transactions/${id}`, body);
    },
  );
export const useBulkCategory = () =>
  useBooksChange((v: { ids: string[]; categoryId: string | null; payee?: string | null }) =>
    api<{ changed: number }>("POST", "books/transactions/bulk", v),
  );
export const useRemoveTransaction = () => useBooksChange((id: string) => api<{ ok: true }>("DELETE", `books/transactions/${id}`));
export const useUnsplit = () => useBooksChange((id: string) => api<TransactionRow>("POST", `books/transactions/${id}/unsplit`));
export const useTyped = () =>
  useBooksChange((v: { accountId: string; day: string; amountCents: number; description: string; categoryId?: string | null }) =>
    api<TransactionRow>("POST", "books/transactions", v),
  );
export const useImport = () =>
  useBooksChange((v: { accountId: string; kind: "csv" | "ofx"; text: string; flip: boolean }) =>
    api<{ importId: string | null; added: number; skipped: number; bad: number; ruled: number; locked: number }>(
      "POST",
      "books/import",
      v,
    ),
  );
export const useUndoImport = () => useBooksChange((id: string) => api<{ removed: number }>("POST", `books/imports/${id}/undo`));
export const useReconcileLook = () =>
  useMutation({
    mutationFn: (v: { accountId: string; endDay: string }) =>
      api<{ beginCents: number; from: string; first: boolean; transactions: TransactionRow[] }>(
        "POST",
        "books/reconcile/look",
        v,
      ),
  });
export const useReconcile = () =>
  useBooksChange((v: { accountId: string; endDay: string; endBalanceCents: number; ids: string[] }) =>
    api<{ id: string }>("POST", "books/reconcile", v),
  );
export const useSplitPayouts = () =>
  useBooksChange((year: number) => api<{ split: number }>("POST", "books/payouts/split", { year }));
export const useAdjust = () =>
  useBooksChange((v: { day: string; memo: string; lines: { categoryId: string; amountCents: number }[] }) =>
    api<{ id: string }>("POST", "books/adjustments", v),
  );
export const useUndoAdjust = () => useBooksChange((id: string) => api<{ ok: true }>("POST", `books/adjustments/${id}/undo`));
export const useSavePayee = () =>
  useBooksChange((v: { key: string; w9?: boolean; personId?: string | null }) => {
    const { key, ...body } = v;
    return api<{ ok: true }>("PUT", `books/1099/${encodeURIComponent(key)}`, body);
  });
export const usePlaidLinkToken = () =>
  useMutation({
    mutationFn: (connectionId?: string) =>
      api<{ linkToken: string }>("POST", "books/plaid/link-token", connectionId ? { connectionId } : {}),
  });
export const usePlaidConnect = () =>
  useBooksChange(async (publicToken: string) => {
    await api<{ added: number }>("POST", "books/plaid/connect", { publicToken });
    return api<{ added: number }>("POST", "books/plaid/sync");
  });
export const usePlaidSync = () => useBooksChange(() => api<{ added: number }>("POST", "books/plaid/sync"));

// Reports, receipts, reconcile history and closing the books ---------------------------------------------------------

export const useLedger = (companyId: string, from: string, to: string, categoryId?: string, enabled = true) =>
  useQuery({
    queryKey: ["books-ledger", companyId, from, to, categoryId ?? ""],
    queryFn: () => api<{ lines: LedgerLine[]; more: boolean }>("GET", `books/ledger${qs({ from, to, categoryId })}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useBrandPnl = (companyId: string, from: string, to: string, enabled: boolean) =>
  useQuery({
    queryKey: ["books-companies", companyId, from, to],
    queryFn: () =>
      api<{ companyId: string; name: string; incomeCents: number; expenseCents: number; profitCents: number }[]>(
        "GET",
        `books/companies${qs({ from, to })}`,
      ),
    enabled,
  });

export const useReconciles = (companyId: string) =>
  useQuery({
    queryKey: ["reconciles", companyId],
    queryFn: () => api<{ reconciles: ReconcileRow[] }>("GET", "books/reconciles"),
  });
export const useUndoReconcile = () => useBooksChange((id: string) => api<{ ok: true }>("POST", `books/reconciles/${id}/undo`));

export const useLock = () =>
  useBooksChange((day: string | null) => api<{ lockDay: string | null }>("PUT", "books/lock", { day }));

export const useReceipts = (transactionId: string) =>
  useQuery({
    queryKey: ["receipts", transactionId],
    queryFn: () => api<{ files: FileRow[] }>("GET", `books/transactions/${transactionId}/receipts`),
  });
export const useAddReceipt = () =>
  useBooksChange((v: { transactionId: string; name: string; mime: string; dataBase64: string }) => {
    const { transactionId, ...body } = v;
    return api<FileRow>("POST", `books/transactions/${transactionId}/receipts`, body);
  });
export const useRemoveReceipt = () => useBooksChange((id: string) => api<{ ok: true }>("DELETE", `books/receipts/${id}`));

// Review: post, exclude, pair and match -------------------------------------------------------------------------------

interface Reviewed {
  changed: number;
  skipped: number;
}
/** The AI reviewer puts a category and a note on pending lines, and posts the sure ones when Books settings say so. */
export const useAiReview = () =>
  useBooksChange((b: { accountId?: string; again?: boolean }) => api<AiReviewAnswer>("POST", "books/transactions/ai-review", b));
/** The last tax savings list, and a new one (3 to 5 minutes, Claude reads this year's rules on the web). */
export const useBookTax = (companyId: string) =>
  useQuery({ queryKey: ["books", companyId, "tax"], queryFn: () => api<TaxAnswer>("GET", "books/tax") });
export const useRunTaxReview = () => useBooksChange((b: { year?: number }) => api<TaxAnswer>("POST", "books/tax/review", b));
export const usePostLines = () => useBooksChange((ids: string[]) => api<Reviewed>("POST", "books/transactions/post", { ids }));
export const useExcludeLines = () =>
  useBooksChange((ids: string[]) => api<Reviewed>("POST", "books/transactions/exclude", { ids }));
export const useIncludeLines = () =>
  useBooksChange((ids: string[]) => api<Reviewed>("POST", "books/transactions/include", { ids }));
export const useMatches = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ["books-match", id],
    queryFn: () => api<MatchesAnswer>("GET", `books/transactions/${id}/matches`),
    enabled,
  });
export const usePair = () =>
  useBooksChange((v: { id: string; otherId: string }) =>
    api<TransactionRow>("POST", `books/transactions/${v.id}/pair`, { otherId: v.otherId }),
  );
export const useUnpair = () => useBooksChange((id: string) => api<TransactionRow>("POST", `books/transactions/${id}/unpair`));
export const useMatchPayments = () =>
  useBooksChange((v: { id: string; paymentIds: string[] }) =>
    api<TransactionRow>("POST", `books/transactions/${v.id}/match`, { paymentIds: v.paymentIds }),
  );
export const useUnmatch = () => useBooksChange((id: string) => api<TransactionRow>("POST", `books/transactions/${id}/unmatch`));
export const usePayout = () => useBooksChange((id: string) => api<TransactionRow>("POST", `books/transactions/${id}/payout`));
